import type { TextInputProps, ViewProps } from "react-native"

import React from "react"
import { TextInput as InputBase, StyleSheet, View } from "react-native"

import type { TestId } from "../test-id"

import { Box } from "../Box"
import { Row } from "../Row"
import { testIds } from "../test-id"
import { AppText } from "../Text"
import { SPACING } from "../theme"
import { useFormControlContext } from "./FormControlProvider"
import { useInputStyle } from "./useInputStyle"
import { useCurrentColorMode } from "@/theme/AppThemeContextProvider"

const styles = StyleSheet.create({
  inputRightAddonText: {
    paddingRight: SPACING.$4,
  },
})

export function InputRightAddon({ children, ...rest }: ViewProps) {
  return (
    <View {...rest}>
      {typeof children === "string" ? (
        <AppText style={styles.inputRightAddonText}>{children}</AppText>
      ) : (
        children
      )}
    </View>
  )
}

export interface InputProps extends Omit<TextInputProps, "id"> {
  InputRightElement?: React.JSX.Element | null
  id?: TestId
  isDisabled?: boolean
  isInvalid?: boolean
}

export const Input = React.forwardRef(function Input(
  {
    id,
    InputRightElement,
    isDisabled,
    isInvalid,
    multiline,
    numberOfLines,
    style,
    textContentType,
    ...rest
  }: InputProps,
  ref: React.Ref<InputBase>,
) {
  const colorMode = useCurrentColorMode()
  const ctx = useFormControlContext()
  if (typeof isDisabled === "undefined") {
    isDisabled = ctx.isDisabled
  }
  if (typeof isInvalid === "undefined") {
    isInvalid = ctx.isInvalid
  }

  const { placeholderTextColor, ...baseStyle } = useInputStyle({
    isDisabled,
    isInvalid,
  })

  const element = (
    <InputBase
      {...testIds(id)}
      ref={ref}
      accessibilityLabel={rest.placeholder}
      accessibilityLabelledBy={ctx.id}
      accessibilityState={{ disabled: isDisabled }}
      autoFocus={ctx.autoFocus}
      keyboardAppearance={colorMode}
      maxFontSizeMultiplier={1.35}
      multiline={multiline}
      //Note: New prop
      // https://reactnative.dev/docs/textinput#numberoflines-android
      // rows={rows}
      // Note: This is Android only
      // see https://reactnative.dev/docs/textinput#numberoflines-android
      numberOfLines={numberOfLines}
      placeholderTextColor={placeholderTextColor}
      readOnly={isDisabled}
      style={[baseStyle, style]}
      secureTextEntry={
        textContentType === "password" || textContentType === "newPassword"
      }
      {...rest}
    />
  )
  if (InputRightElement) {
    return (
      <Row flexWrap={"nowrap"} position={"relative"}>
        <Box flex={1}>{element}</Box>
        <Box
          alignItems={"center"}
          bottom={"$0"}
          display={"flex"}
          justifyContent={"center"}
          position={"absolute"}
          right={"$0"}
          top={"$0"}
        >
          <Box m={"auto"}>{InputRightElement}</Box>
        </Box>
      </Row>
    )
  }
  return element
})
